import type { User } from 'oidc-client-ts'
import type AuthenticationContext from './AuthenticationContext'
import { getImpersonatedUserId } from './impersonationStore'
import { extractProfileClaims } from './profileClaims'
import userManager from './userManager'

export const NOT_AUTHENTICATED_MESSAGE = 'Not authenticated. Please sign in and try again.'

type RequireAuthContextOptions = {
  /**
   * The access token that just failed with a 401 — a token that passed the local expiry
   * check but was rejected by the server. A renewal is triggered only if the stored token
   * still matches it; a concurrent caller may already have renewed past it, in which case
   * that newer token is reused instead of renewing again.
   */
  staleAccessToken?: string
}

let renewal: Promise<User | null> | undefined

/**
 * Concurrent callers share one renewal. A page load runs many queries at once, so an expiry
 * hits them together; Keycloak rotates refresh tokens, and parallel grants presenting the same
 * one would leave every loser holding an invalidated session.
 */
const renewSession = (): Promise<User | null> =>
  (renewal ??= userManager
    .signinSilent()
    .catch(() => null)
    .finally(() => {
      renewal = undefined
    }))

const requireAuthContext = async ({
  staleAccessToken,
}: RequireAuthContextOptions = {}): Promise<AuthenticationContext> => {
  let user = await userManager.getUser()
  if (!user || !user.access_token || user.expired || user.access_token === staleAccessToken) {
    user = await renewSession()
  }
  if (!user || !user.access_token || user.expired) throw new Error(NOT_AUTHENTICATED_MESSAGE)

  const { organizationId, userEntityId, syncUserId } = extractProfileClaims(user.profile)
  if (!organizationId || !userEntityId || !syncUserId)
    throw new Error(
      'Authentication context is missing. This may be because your user account is not linked to a user entity. Please contact support for assistance.'
    )

  return {
    accessToken: user.access_token,
    organizationId,
    userEntityId,
    syncUserId,
    impersonatedUserId: getImpersonatedUserId(),
  }
}

export default requireAuthContext
